/**
 * Pure annotation model for the hand-rolled canvas editor (plan §5.4 / D2).
 *
 * The editor state is an append-only op stack with undo/redo. All state
 * transitions and drawing geometry live here as pure functions so they can be
 * unit-tested without a DOM/canvas; the React component (`AnnotationEditor`)
 * owns only the pointer wiring and the `CanvasRenderingContext2D` calls.
 *
 * Annotations are stored in the bitmap's native pixel space, so replaying the
 * stack onto the full-resolution canvas reproduces them exactly regardless of
 * the on-screen display scale.
 */

/** Available annotation colors — red is the default (plan D2). */
export type AnnotationColor = 'red' | 'yellow' | 'blue';

/** CSS hex for each palette color. */
export const ANNOTATION_COLORS: Record<AnnotationColor, string> = {
  red: '#DC2626',
  yellow: '#EAB308',
  blue: '#2563EB',
};

/** The default drawing tool is the rectangle (plan D2). */
export const DEFAULT_COLOR: AnnotationColor = 'red';

export interface RectAnnotation {
  type: 'rect';
  x: number;
  y: number;
  w: number;
  h: number;
  color: AnnotationColor;
  strokeWidth: number;
}

export interface ArrowAnnotation {
  type: 'arrow';
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: AnnotationColor;
  strokeWidth: number;
}

export interface TextAnnotation {
  type: 'text';
  x: number;
  y: number;
  text: string;
  color: AnnotationColor;
  fontSize: number;
}

export type Annotation = RectAnnotation | ArrowAnnotation | TextAnnotation;

export type AnnotationTool = 'rect' | 'arrow' | 'text';

/** The default active tool (plan D2). */
export const DEFAULT_TOOL: AnnotationTool = 'rect';

/**
 * Editor history. `committed` is the visible stack (oldest→newest); `undone`
 * holds popped annotations available for redo. Any new commit clears `undone`
 * (standard linear-history semantics).
 */
export interface AnnotationState {
  committed: Annotation[];
  undone: Annotation[];
}

export function createInitialState(): AnnotationState {
  return { committed: [], undone: [] };
}

/** Append an annotation, clearing the redo stack. Returns a new state. */
export function addAnnotation(state: AnnotationState, annotation: Annotation): AnnotationState {
  return { committed: [...state.committed, annotation], undone: [] };
}

export function canUndo(state: AnnotationState): boolean {
  return state.committed.length > 0;
}

export function canRedo(state: AnnotationState): boolean {
  return state.undone.length > 0;
}

/** Move the most recent committed annotation onto the redo stack. */
export function undo(state: AnnotationState): AnnotationState {
  if (!canUndo(state)) return state;
  const committed = state.committed.slice();
  const last = committed.pop()!;
  return { committed, undone: [...state.undone, last] };
}

/** Re-apply the most recently undone annotation. */
export function redo(state: AnnotationState): AnnotationState {
  if (!canRedo(state)) return state;
  const undone = state.undone.slice();
  const restored = undone.pop()!;
  return { committed: [...state.committed, restored], undone };
}

/**
 * Normalize a drag (start→current) into a top-left origin rect with
 * non-negative width/height, so dragging in any direction yields a valid rect.
 */
export function normalizeRect(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): { x: number; y: number; w: number; h: number } {
  return {
    x: Math.min(x0, x1),
    y: Math.min(y0, y1),
    w: Math.abs(x1 - x0),
    h: Math.abs(y1 - y0),
  };
}

/**
 * Compute the two wing endpoints of an arrowhead at (x2,y2) for a line coming
 * from (x1,y1). `size` is the wing length in pixels; `spread` the half-angle
 * in radians. Pure geometry — unit-tested independently of canvas.
 */
export function arrowHeadPoints(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  size: number,
  spread = Math.PI / 7,
): [{ x: number; y: number }, { x: number; y: number }] {
  const angle = Math.atan2(y2 - y1, x2 - x1);
  return [
    { x: x2 - size * Math.cos(angle - spread), y: y2 - size * Math.sin(angle - spread) },
    { x: x2 - size * Math.cos(angle + spread), y: y2 - size * Math.sin(angle + spread) },
  ];
}

/**
 * Draw a single annotation onto a 2D context in the canvas's own pixel space.
 * Kept here (rather than in the component) so the render path is exercised by
 * the same tests that cover the model; callers pass a real or mocked context.
 */
export function drawAnnotation(ctx: CanvasRenderingContext2D, annotation: Annotation): void {
  const color = ANNOTATION_COLORS[annotation.color];
  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';

  if (annotation.type === 'rect') {
    ctx.lineWidth = annotation.strokeWidth;
    ctx.strokeRect(annotation.x, annotation.y, annotation.w, annotation.h);
  } else if (annotation.type === 'arrow') {
    ctx.lineWidth = annotation.strokeWidth;
    ctx.beginPath();
    ctx.moveTo(annotation.x1, annotation.y1);
    ctx.lineTo(annotation.x2, annotation.y2);
    ctx.stroke();

    const headSize = Math.max(annotation.strokeWidth * 4, 12);
    const [w1, w2] = arrowHeadPoints(
      annotation.x1,
      annotation.y1,
      annotation.x2,
      annotation.y2,
      headSize,
    );
    ctx.beginPath();
    ctx.moveTo(w1.x, w1.y);
    ctx.lineTo(annotation.x2, annotation.y2);
    ctx.lineTo(w2.x, w2.y);
    ctx.stroke();
  } else {
    ctx.font = `${annotation.fontSize}px system-ui, -apple-system, sans-serif`;
    ctx.textBaseline = 'top';
    ctx.fillText(annotation.text, annotation.x, annotation.y);
  }
  ctx.restore();
}

/** Replay the whole committed stack onto a context (oldest first). */
export function drawAnnotations(ctx: CanvasRenderingContext2D, annotations: Annotation[]): void {
  for (const annotation of annotations) drawAnnotation(ctx, annotation);
}
